import React from 'react';
import {
  activateAppUpdate,
  CHUNK_UPDATE_NEEDED_EVENT,
  wasUpdateRecentlyAttempted,
} from '../utils/appUpdate';

/**
 * Catches React.lazy chunk failures (typically a stale tab requesting a chunk
 * that a newer deploy removed from the server).
 *
 * First failure in a session: silently activate the current app version and
 * reload — a plain reload fetches fresh HTML via the SW navigation route, so
 * this deterministically heals the tab without bothering the user.
 *
 * Repeat failure within the update-attempt window: automatic recovery did not
 * work, so surface the update banner with forceRecovery (unregister the SW and
 * do a cache-busted reload) and let the user trigger it.
 */
export default class ChunkErrorBoundary extends React.Component {
  state = { chunkFailed: false };

  static getDerivedStateFromError(error) {
    if (isChunkLoadError(error)) {
      return { chunkFailed: true };
    }
    return null;
  }

  componentDidCatch(error) {
    if (!isChunkLoadError(error)) throw error;

    const registration = window.__swWaitingRegistration || null;

    if (!wasUpdateRecentlyAttempted()) {
      // First failure: recover automatically instead of showing a popup.
      window.__chunkUpdateNotice = null;
      activateAppUpdate(registration).catch(() => {
        // Recovery failed outright; fall back to the banner.
        dispatchChunkUpdateNeeded({ forceRecovery: true, registration });
      });
      return;
    }

    // A recent update attempt already happened and chunks are still failing;
    // ask the user to run the aggressive recovery path.
    dispatchChunkUpdateNeeded({ forceRecovery: true, registration });
  }

  render() {
    if (this.state.chunkFailed) {
      return (
        <div className="app-loading">
          <p>Loading…</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function dispatchChunkUpdateNeeded(detail) {
  window.__chunkUpdateNotice = detail;
  window.dispatchEvent(
    new CustomEvent(CHUNK_UPDATE_NEEDED_EVENT, {
      detail,
    }),
  );
}

function isChunkLoadError(error) {
  if (error?.name === 'ChunkLoadError') return true;
  const msg = String(error?.message || '');
  return msg.includes('Loading chunk') || msg.includes('Loading CSS chunk');
}
